import { Button, ButtonLink } from '@/components/ui/button';
import type { CompanySearchResult } from '@dash-lite/sdk';
import { Building2, Lock, MapPin, RefreshCw, SearchX } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { CompanySheet, CompanyStatusBadge } from '@/components/company/company-sheet';
import { Breadcrumb, Page } from '@/components/layout/page';
import { OfferCard } from '@/components/payment/offer-card';
import { DevNote } from '@/components/ui/dev-note';
import { Alert, Badge, Card, CardBody, Container, EmptyState, Section, Skeleton } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { getCompanyWithMeta  } from '@/lib/dashlite-envelope';
import type {CompanyEnvelope} from '@/lib/dashlite-envelope';
import { errorCode, isApiError, presentError } from '@/lib/errors';
import { formatCountry, formatDateTime } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Fiche d'une entreprise — l'écran qui matérialise la règle métier centrale
 * de la plateforme : **aucune fiche complète sans droit d'accès actif**.
 *
 * Trois réponses possibles, trois écrans :
 *
 * | Réponse de l'API                | Ce que voit le visiteur                    |
 * | ------------------------------- | ------------------------------------------ |
 * | `200` + `{ data, meta }`        | la fiche complète (+ bandeau si `stale`)   |
 * | `401 unauthenticated`           | l'aperçu verrouillé et l'offre du site     |
 * | `403 entitlement_required`      | idem — il a un compte, pas encore d'accès  |
 * | `404 company_not_found`         | « entreprise introuvable »                 |
 * | `503 upstream_unavailable`      | panne temporaire, avec un bouton réessayer |
 *
 * Le mur payant n'est donc pas décidé côté front : il est la conséquence
 * d'une réponse de l'API. C'est ce qui garantit qu'aucune donnée protégée
 * n'a transité vers le navigateur.
 */
export default function CompanyPage() {
    const { registrationNumber = '' } = useParams();
    const location = useLocation();
    const { isAuthenticated, hasAccess } = useSession();

    /**
     * Aperçu éventuel transporté depuis la liste de résultats : il permet
     * d'afficher le nom de l'entreprise sans attendre — et même lorsque la
     * fiche complète est refusée.
     */
    const preview = (location.state as { preview?: CompanySearchResult } | null)?.preview ?? null;

    const [envelope, setEnvelope] = useState<CompanyEnvelope | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            setEnvelope(await getCompanyWithMeta(registrationNumber));
        } catch (caught) {
            setEnvelope(null);
            setError(caught);
        } finally {
            setLoading(false);
        }
    }, [registrationNumber]);

    /**
     * Chargement de la fiche, une fois par identifiant.
     *
     * L'identifiant déjà chargé est mémorisé dans une référence, ce qui
     * neutralise le double montage de `StrictMode` en développement : sans
     * cela, chaque affichage produirait deux appels identiques.
     */
    const loadedNumber = useRef<string | null>(null);

    useEffect(() => {
        if (loadedNumber.current === registrationNumber) {
            return;
        }

        loadedNumber.current = registrationNumber;
        void load();
    }, [load, registrationNumber]);

    /**
     * Réouverture après achat.
     *
     * Quand le droit d'accès s'ouvre — au retour du tunnel de paiement, ou
     * après une connexion — on rejoue l'appel qui avait été refusé. La
     * condition porte sur l'erreur précédente, pas sur `hasAccess` seul :
     * sans cela, la simple arrivée de `GET /me` déclencherait un second
     * appel identique alors que la fiche est déjà à l'écran.
     */
    useEffect(() => {
        if (hasAccess && isApiError(error, 'entitlement_required', 'unauthenticated')) {
            void load();
        }
    }, [hasAccess, error, load]);

    const title = envelope?.data.name ?? preview?.name ?? registrationNumber;

    return (
        <Page title={title}>
            <Container className="py-8">
                <Breadcrumb
                    items={[
                        { label: 'Accueil', to: paths.home },
                        { label: 'Recherche', to: paths.search },
                        { label: title },
                    ]}
                />
            </Container>

            <Section className="py-0 pb-16">
                <Container className="space-y-6">
                    <CompanyHeading
                        name={envelope?.data.name ?? preview?.name ?? null}
                        registrationNumber={registrationNumber}
                        city={envelope?.data.address.city ?? preview?.city ?? null}
                        country={envelope?.data.country ?? preview?.country ?? null}
                        legalForm={envelope?.data.legal_form ?? preview?.legal_form ?? null}
                        status={envelope?.data.status ?? null}
                        loading={loading}
                    />

                    {loading && <SheetSkeleton />}

                    {!loading && envelope && (
                        <>
                            {envelope.meta.stale && <StaleNotice fetchedAt={envelope.meta.fetched_at} onRetry={load} />}

                            {/* Repère du robot de vérification : présent dès
                                que l'accès est effectivement ouvert. */}
                            <div data-dashlite="access-granted" className="sr-only">
                                Votre accès est ouvert.
                            </div>

                            <CompanySheet company={envelope.data} />
                        </>
                    )}

                    {!loading && error !== null && (
                        <CompanyError
                            error={error}
                            registrationNumber={registrationNumber}
                            isAuthenticated={isAuthenticated}
                            onRetry={load}
                        />
                    )}

                    <DevNote
                        title="Ce que fait cet écran"
                        endpoints={['GET /companies/{registration_number}', 'GET /entitlement']}
                    >
                        <p>
                            La fiche complète exige un droit d'accès actif. Sans session, l'API répond{' '}
                            <code className="font-mono">401 unauthenticated</code> ; avec une session mais sans accès,{' '}
                            <code className="font-mono">403 entitlement_required</code>. Dans les deux cas, aucune
                            donnée protégée n'a été envoyée au navigateur.
                        </p>
                        <p>
                            Cet appel lit l'enveloppe complète (<code className="font-mono">{'{ data, meta }'}</code>)
                            plutôt que de passer par le SDK, afin de récupérer{' '}
                            <code className="font-mono">meta.stale</code> : en cas de panne du fournisseur, l'API sert
                            une fiche datée plutôt qu'une erreur, et le signale.
                        </p>
                        <p>
                            Les fiches sont mises en cache 7 jours et renouvelées en arrière-plan passé la moitié de
                            leur durée de vie : vous recevez toujours la réponse immédiatement.
                        </p>
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}

function CompanyHeading({
    name,
    registrationNumber,
    city,
    country,
    legalForm,
    status,
    loading,
}: {
    name: string | null;
    registrationNumber: string;
    city: string | null;
    country: string | null;
    legalForm: string | null;
    status: Parameters<typeof CompanyStatusBadge>[0]['status'] | null;
    loading: boolean;
}) {
    return (
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
                <span className="bg-brand-50 text-brand-600 flex size-12 shrink-0 items-center justify-center rounded-xl">
                    <Building2 className="size-6" aria-hidden />
                </span>

                <div className="min-w-0">
                    {name ? (
                        <h1 className="font-display truncate text-3xl tracking-tight">{name}</h1>
                    ) : loading ? (
                        <Skeleton className="h-9 w-64" />
                    ) : (
                        <h1 className="font-display text-3xl tracking-tight">Entreprise {registrationNumber}</h1>
                    )}

                    <div className="text-ink-muted mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                        <span className="font-mono">{registrationNumber}</span>
                        {city && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="size-3.5" aria-hidden />
                                {city}
                            </span>
                        )}
                        {country && <span>{formatCountry(country)}</span>}
                        {legalForm && <span className="truncate">{legalForm}</span>}
                    </div>
                </div>
            </div>

            {status && <CompanyStatusBadge status={status} />}
        </div>
    );
}

/**
 * `meta.stale` : la fiche vient du cache parce que le fournisseur ne répondait
 * pas. On le dit plutôt que de le taire — et on propose de réessayer.
 */
function StaleNotice({ fetchedAt, onRetry }: { fetchedAt: string | undefined; onRetry: () => void }) {
    return (
        <Alert
            tone="caution"
            title="Données datées"
            action={
                <Button variant="secondary" size="sm" onClick={onRetry}>
                    <RefreshCw className="size-4" aria-hidden />
                    Réessayer
                </Button>
            }
        >
            Le registre officiel n'a pas répondu. Cette fiche a été constituée le{' '}
            <strong>{formatDateTime(fetchedAt)}</strong> et vous est servie telle quelle plutôt que de vous laisser
            sans réponse.
        </Alert>
    );
}

/** Aiguillage des erreurs vers l'écran adapté au code renvoyé. */
function CompanyError({
    error,
    registrationNumber,
    isAuthenticated,
    onRetry,
}: {
    error: unknown;
    registrationNumber: string;
    isAuthenticated: boolean;
    onRetry: () => void;
}) {
    const presentation = presentError(error);

    // Le mur payant : ni compte, ou compte sans accès actif.
    if (isApiError(error, 'entitlement_required', 'unauthenticated')) {
        return <Paywall registrationNumber={registrationNumber} isAuthenticated={isAuthenticated} />;
    }

    if (isApiError(error, 'company_not_found')) {
        return (
            <EmptyState
                icon={<SearchX className="size-8" />}
                title="Aucune entreprise ne correspond à cet identifiant"
                description={
                    <>
                        L'identifiant <span className="font-mono">{registrationNumber}</span> est inconnu du registre,
                        ou mal formé pour ce pays. Les deux cas renvoient volontairement le même code.
                    </>
                }
                action={
                    <ButtonLink to={paths.search} variant="secondary" size="sm">
                        Lancer une nouvelle recherche
                    </ButtonLink>
                }
            />
        );
    }

    return (
        <Alert
            tone={presentation.retryable ? 'caution' : 'critical'}
            title={presentation.message}
            action={
                presentation.retryable ? (
                    <Button variant="secondary" size="sm" onClick={onRetry}>
                        <RefreshCw className="size-4" aria-hidden />
                        Réessayer
                    </Button>
                ) : undefined
            }
        >
            <p>{presentation.hint}</p>
            {errorCode(error) && (
                <p className="mt-2 font-mono text-xs opacity-70">error.code = {errorCode(error)}</p>
            )}
        </Alert>
    );
}

/**
 * Mur payant : ce que voit un visiteur sans accès actif.
 *
 * Le contenu masqué n'est pas « caché en CSS » — il n'a jamais été envoyé.
 * La liste ci-dessous décrit ce que contient la fiche, elle n'en révèle rien.
 */
function Paywall({ registrationNumber, isAuthenticated }: { registrationNumber: string; isAuthenticated: boolean }) {
    return (
        <div className="grid items-start gap-8 lg:grid-cols-[1.1fr_1fr]">
            <Card className="border-line-strong">
                <CardBody className="space-y-5 p-7">
                    <Badge tone="caution">
                        <Lock className="size-3.5" aria-hidden />
                        Fiche complète verrouillée
                    </Badge>

                    <h2 className="text-ink font-sans text-xl font-semibold">
                        Cette fiche nécessite un accès actif
                    </h2>

                    <p className="text-ink-soft text-sm leading-relaxed">
                        {isAuthenticated
                            ? "Votre compte est bien connecté, mais aucun droit d'accès n'est actif. Choisissez l'offre du site pour ouvrir la consultation."
                            : "Aucun compte n'est nécessaire pour commencer : il est créé automatiquement à la réussite du paiement, et vous recevrez un lien pour choisir votre mot de passe."}
                    </p>

                    <ul className="text-ink-soft space-y-2 text-sm">
                        {[
                            'Immatriculation, numéro de gestion et greffe compétent',
                            'Capital social, activité et code d’activité',
                            'Dirigeants, avec dates de nomination',
                            'Effectifs, établissements et siège social',
                            'Identifiant européen (EUID) et juridiction compétente',
                        ].map((item) => (
                            <li key={item} className="flex items-start gap-2.5">
                                <Lock className="text-ink-muted mt-0.5 size-3.5 shrink-0" aria-hidden />
                                {item}
                            </li>
                        ))}
                    </ul>

                    {isAuthenticated && (
                        <p className="text-ink-muted text-xs">
                            Déjà payé&nbsp;?{' '}
                            <ButtonLink to={paths.account} variant="ghost" size="sm" className="px-1">
                                Vérifier mon accès
                            </ButtonLink>
                        </p>
                    )}

                    {!isAuthenticated && (
                        <p className="text-ink-muted text-xs">
                            Vous avez déjà un compte&nbsp;?{' '}
                            <ButtonLink to={paths.login} variant="ghost" size="sm" className="px-1">
                                Se connecter
                            </ButtonLink>
                        </p>
                    )}
                </CardBody>
            </Card>

            {/* Le contexte d'achat : l'entreprise consultée est attachée au
                paiement, et se retrouvera sur le droit d'accès. */}
            <OfferCard returnTo={registrationNumber} />
        </div>
    );
}

function SheetSkeleton() {
    return (
        <div className="space-y-6">
            {[0, 1].map((block) => (
                <Card key={block}>
                    <CardBody className="space-y-3">
                        {[0, 1, 2, 3, 4].map((row) => (
                            <Skeleton key={row} className="h-5 w-full" />
                        ))}
                    </CardBody>
                </Card>
            ))}
        </div>
    );
}
